import React, { useState } from 'react';
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Box,
  Alert,
} from '@mui/material';
import { classApi } from '../api';

interface ClassRefundProps {
  classItem: {
    id: string;
    title: string;
    date: string;
    startTime: string;
    price?: number;
  };
  registrationId: string;
  facilityId: string;
  businessId: string;
  onSuccess?: () => void;
}

const ClassRefund: React.FC<ClassRefundProps> = ({
  classItem,
  registrationId,
  businessId,
  facilityId,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [refundAmount, setRefundAmount] = useState<number | null>(null);
  const [newBalance, setNewBalance] = useState<number | null>(null);

  const handleRefund = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await classApi.refundClassRegistration(
        businessId,
        facilityId,
        classItem.id,
        registrationId
      );
      
      setRefundAmount(data.refundAmount);
      setNewBalance(data.newBalance);
      setOpenDialog(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Refund failed');
    } finally {
      setLoading(false);
    }
  };

  const classDateTime = new Date(`${classItem.date}T${classItem.startTime}`);
  const hoursUntilClass = (classDateTime.getTime() - new Date().getTime()) / (1000 * 60 * 60);
  const canRefund = hoursUntilClass >= 24;

  return (
    <>
      <Button
        variant="outlined"
        color="error"
        onClick={() => setOpenDialog(true)}
        disabled={!canRefund}
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          '&:hover': {
            backgroundColor: 'error.light',
            color: 'white',
          },
        }}
      >
        Cancel Registration
      </Button>

      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: '400px',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Cancel Registration
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to cancel your registration for {classItem.title}?
          </DialogContentText>
          
          {canRefund && classItem.price ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              You will receive ${classItem.price} in credits to your account.
            </Alert>
          ) : (
            <Alert severity="error" sx={{ mb: 2 }}>
              Note: Cancellations are only available 24 hours or more before the class start time.
              {!canRefund && (
                <Box component="div" sx={{ mt: 1 }}>
                  This class starts in less than 24 hours, so cancellation is not available.
                </Box>
              )}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {refundAmount !== null && newBalance !== null && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Successfully refunded ${refundAmount}. Your new credit balance is ${newBalance}.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setOpenDialog(false)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRefund}
            color="error"
            disabled={loading || !canRefund}
            variant="contained"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'error.dark',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Confirm Cancellation'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClassRefund; 